import store from '@/store'
import {RoleGroups, CompanyEntityStatus} from '@/util/constants'
import {MASTER_LOT_STATUSES} from '@/util/refinery/upstream/constants'
import {canAccessPreSettlementTermsPage} from '@/util/preSettlement'
import {isFeatureFlagEnabled, FEATURE_FLAGS} from '@/mixins/featureFlags'
const {
  PRINCIPAL_OWNER_ROLE_IDS,
  AUTHORIZED_TRADER_ROLE_IDS,
  SALES_MANAGER_ROLE_IDS,
  SALES_REP_ROLE_IDS,
  ACCOUNTING_ROLE_IDS,
  API_ROLE_IDS,
} = RoleGroups

// confirm that we have an authToken in storage and a current user.
const isAuthenticated = () => {
  const token = store.getters.getAccessToken
  return token !== null
}

const unauthenticatedOnly = (to, from, next) => {
  if (isAuthenticated()) {
    return {name: `dashboard`}
  }
}

const authenticatedOnly = (to, from, next) => {
  if (!isAuthenticated()) {
    return {name: `login`, query: {redirect: to ? to.path : undefined}}
  } else {
    const profile = store.getters.getUserProfile
    if (profile.passwordResetRequired) {
      return {
        name: 'forcePasswordReset',
        query: {redirect: to ? to.path : undefined},
      }
    }
  }
}

const passwordResetOnly = (to, from, next) => {
  const profile = store.getters.getUserProfile
  if (profile.passwordResetRequired !== true) {
    return {name: `dashboard`}
  }
}

const adminOnly = (to, from, next) => {
  const profile = store.getters.getUserProfile
  if (profile.isAdmin !== true) {
    return {name: `dashboard`}
  }
}

const companyOnly = (to, from, next) => {
  if (!store.getters.getSelectedCompany) {
    return {name: `dashboard`}
  }
}

//If the company is not active the only page they should have access to is the Comp App Page.
const fullAccessCompanyOnly = () => {
  const company = store.getters.getSelectedCompany || {}
  const profile = store.getters.getUserProfile
  if (profile.isAdmin == true) return undefined;
  if (company.active) return undefined;
  if (!company) return {name: `dashboard`};
  if (CompanyEntityStatus.LEAD_STATUS_IDS.includes(company.entityStatus)) return {name: `application`};
  return {name: `dashboard`};
}


const leadCompany = () => {
  const company = store.getters.getSelectedCompany || {}
  const isLead = CompanyEntityStatus.LEAD_STATUS_IDS.includes(company.entityStatus);
  return isLead
    ? undefined
    : {name: `dashboard`};
}

const nonEnhancedModeOnly = (to, from, next) => {
  if (store.getters.getSelectedCompany.enhancedMode) {
    return {name: `dashboard`}
  }
}

const limitOrdersEnabledOnly = (to, from, next) => {
  if (!store.getters.getSelectedCompany.enableLimitOrders) {
    return {name: `dashboard`}
  }
}

const masterLotsEnabledOnly = (to, from, next) => {
  if (!isFeatureFlagEnabled(store, FEATURE_FLAGS.MASTER_LOTS_ENABLED)) {
    return {name: `dashboard`}
  }
}

const preSettlementTermsPageOnly = () => {
  return canAccessPreSettlementTermsPage(store.getters.getSelectedCompany)
    ? undefined
    : {name: 'dashboard'}
}

const bullionWalletAccountSummaryEnabledOnly = () => {
  if (!isFeatureFlagEnabled(store, FEATURE_FLAGS.BULLION_WALLET_ACCOUNT_SUMMARY_ENABLED)) {
    return {name: 'AccountBalances'}
  }
}

// Master lot edit route is composition-only. A lot whose status is past `draft`
// has been shipped/received/etc. and its composition is locked; redirect to
// the read-only detail view. Unknown / not-yet-loaded lots ALSO bounce to
// detail: deep-link arrivals hit this guard before the store hydrates, and
// pass-through would briefly expose the editor for non-editable lots.
// `masterLotDetail` is the safe default — it tolerates every status and will
// surface an Edit affordance when the loaded lot is actually a draft.
const editableMasterLotOnly = (to) => {
  const masterLot = store.getters['upstreamMasterLots/getMasterLotById']?.(to.params.id)
  if (!masterLot || masterLot.status !== MASTER_LOT_STATUSES.DRAFT) {
    return {name: 'masterLotDetail', params: {id: to.params.id}}
  }
}

const hasMfaToken = (to, from, next) => {
  if (store.getters.getMfaToken) {
    return
  }
  return {name: `dashboard`}
}

const hasMfaTokenOrAuthToken = (to, from, next) => {
  if (store.getters.getMfaToken || store.getters.getAccessToken) {
    return
  }
  return {name: `dashboard`}
}

const equityTrustEligibleOnly = (to, from, next) => {
  const company = store.getters.getSelectedCompany
  if (!company) {
    return {name: `dashboard`}
  }
  if (
    company.equityTrustContactId &&
    salesManagersOnly(to, from, next) === undefined
  ) {
    return
  }
  return {name: `dashboard`}
}

const principalOwnersOnly = (to, from, next) =>
  _permissionsByRole({to, from, next}, PRINCIPAL_OWNER_ROLE_IDS)

const salesManagersOnly = (to, from, next) =>
  _permissionsByRole({to, from, next}, SALES_MANAGER_ROLE_IDS)

const apiAccessOnly = (to, from, next) => {
  const company = store.getters.getSelectedCompany
  if (!company.apiContractEffectiveDate) return {name: `dashboard`}
  return _permissionsByRole({to, from, next}, API_ROLE_IDS)
}

const authorizedTradersOnly = (to, from, next) =>
  _permissionsByRole({to, from, next}, AUTHORIZED_TRADER_ROLE_IDS)

const accountingPermissionsOnly = (to, from, next) =>
  _permissionsByRole({to, from, next}, ACCOUNTING_ROLE_IDS)

const salesRepPermissionsOnly = (to, from, next) =>
  _permissionsByRole({to, from, next}, SALES_REP_ROLE_IDS)

const permissionsByRole = (ROLE_IDS) => {
  return (to, from, next) => _permissionsByRole({to, from, next}, ROLE_IDS)
}

const _permissionsByRole = ({to, from, next}, ROLE_IDS) => {
  if (store.getters.getUserProfile.isAdmin) {
    return
  }
  // @todo - Make company selectable if user has access to multiple
  if (!store.getters.getUserProfile.companyPermissions) {
    return {name: `dashboard`}
  }
  const selectedCompanyId = Object.keys(
    store.getters.getUserProfile.companyPermissions
  )[0]
  const roles = store.getters.getUserProfile.companyPermissions[
    selectedCompanyId
  ].roles.map((role) => role.role.netsuite_internalId)
  for (let i in ROLE_IDS) {
    if (roles.indexOf(ROLE_IDS[i]) > -1) {
      return
    }
  }
  return {name: `dashboard`}
}

const shipSecureEnabled = () => {
  return store.getters.getSelectedCompany?.shipSecure
    ? undefined 
    : {name: `dashboard`};
}

// Guard: allow only companies that have accepted the refinery agreement
const isRefineryCustomer = () => {
  const company = store.getters.getSelectedCompany;
  const hasAccepted = Boolean(company?.refineryAgreementAcceptedAt);
  return hasAccepted ? undefined : {name: `dashboard`};
}

// (Temporarily removed) reacceptance guards; will be reintroduced when DocuSign
// active version check is implemented.

// Guard factory to control Refinery menu visibility.
// isEnroll = is the enrollement route specifically
// Show refinery pages to enrolled customers or admins
// Only show enroll option to non-customers (regardless of admin role)
const isRefineryGuard = (isEnroll = false) => {
  return () => {
    const profile = store.getters.getUserProfile;
    const isAdmin = profile?.isAdmin === true;
    const company = store.getters.getSelectedCompany;
    const hasAccepted = Boolean(company?.refineryAgreementAcceptedAt);
    const isCustomer = hasAccepted === true;
    if (isEnroll) return isCustomer ? {name: `refiningDashboard`} : undefined;
    return (isCustomer || isAdmin) ? undefined : {name: `dashboard`};
  };
};
let companyContextVerified = false;

store.subscribe((mutation) => {
  if (mutation.type === 'SET_ACCESS_TOKEN' && !mutation.payload) {
    companyContextVerified = false;
  }
});

const ensureCompanyContext = async () => {
  if (companyContextVerified) return;
  const token = store.getters.getAccessToken;
  if (!token) return;
  const profile = store.getters.getUserProfile;
  if (!profile?.companyPermissions) return;
  try {
    const company = store.getters.getSelectedCompany;
    if (company?.id) {
      await store.dispatch('refreshSelectedCompany');
    } else {
      await store.dispatch('fetchDefaultSelectedCompany');
    }
    companyContextVerified = true;
  } catch (e) {
    console.error('Failed to refresh company context:', e);
  }
};

const requiresReportingAccess = () => {
  const profile = store.getters.getUserProfile
  if (!profile?.isAdmin) return
  if (profile.can?.reportingAccess) return
  return {name: 'dashboard'}
}

const isCashAdvancesFeatureEnabled = () => {
  const selectedCompanyId = store.getters.getSelectedCompany?.id
  const value = store.getters.getConfig('CASH_ADVANCES_ENABLED')
  if (value === true || value === 'true') return true
  if (value && typeof value === 'object') {
    if (value.enabled === true || value.enabled === 'true') return true
    const companyIds = Array.isArray(value.companyIds) ? value.companyIds.map(String) : []
    return Boolean(selectedCompanyId && companyIds.includes(String(selectedCompanyId)))
  }
  return false
}

const requiresCashAdvanceAdminAccess = () => {
  const profile = store.getters.getUserProfile
  if (!profile?.isAdmin) return
  if (profile.can?.reportingAccess || profile.can?.cashAdvanceApproval) return
  return {name: 'dashboard'}
}

/** Refinery cash advances page — customers when CASH_ADVANCES_ENABLED; admins always (admin UI is role-gated in-page). */
const cashAdvancesPageAccess = () => {
  const profile = store.getters.getUserProfile
  if (profile?.isAdmin) return
  if (isCashAdvancesFeatureEnabled()) return
  return {name: 'dashboard'}
}

// Guard allowing access for admins or companies explicitly flagged as Refining Customers
const vendorOrAdminOnly = () => {
  const profile = store.getters.getUserProfile;
  if (profile?.isAdmin) return;
  const company = store.getters.getSelectedCompany;
  // Only show Refinery (and Learn More) to companies with the NetSuite vendor flag
  // mapped to `refineryCustomer` in the company payload
  return company?.refineryCustomer ? undefined : {name: `dashboard`};
};

export {
  authenticatedOnly,
  unauthenticatedOnly,
  passwordResetOnly,
  adminOnly,
  companyOnly,
  leadCompany,
  fullAccessCompanyOnly,
  authorizedTradersOnly,
  principalOwnersOnly,
  salesManagersOnly,
  shipSecureEnabled,
  accountingPermissionsOnly,
  salesRepPermissionsOnly,
  apiAccessOnly,
  hasMfaToken,
  hasMfaTokenOrAuthToken,
  equityTrustEligibleOnly,
  nonEnhancedModeOnly,
  limitOrdersEnabledOnly,
  masterLotsEnabledOnly,
  preSettlementTermsPageOnly,
  bullionWalletAccountSummaryEnabledOnly,
  editableMasterLotOnly,
  permissionsByRole,
  isRefineryCustomer,
  isRefineryGuard,
  vendorOrAdminOnly,
  requiresReportingAccess,
  requiresCashAdvanceAdminAccess,
  cashAdvancesPageAccess,
  isCashAdvancesFeatureEnabled,
  ensureCompanyContext,
}
